import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AboutSection.css';
import AboutVideo from '../../assets/Home/AboutVideo.mp4';

const AboutSection = () => {
  const wrapperRef = useRef(null);
  const stickyRef = useRef(null);
  const slotRef = useRef(null);
  const videoContainerRef = useRef(null);
  const videoRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(true);

  const updateVideoPosition = useCallback(() => {
    // Disable scroll animation on mobile / tablet screens (width <= 1024px)
    if (window.innerWidth <= 1024) {
      if (videoContainerRef.current) {
        const vEl = videoContainerRef.current;
        vEl.style.top = '';
        vEl.style.left = '';
        vEl.style.width = '';
        vEl.style.height = '';
        vEl.style.borderRadius = '';
      }
      return;
    }

    if (!wrapperRef.current || !stickyRef.current || !slotRef.current || !videoContainerRef.current) {
      return;
    }

    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    const stickyRect = stickyRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    // Total distance wrapper can scroll while pinned
    const totalScroll = wrapperRect.height - windowHeight;
    if (totalScroll <= 0) return;

    // Raw scroll progress between 0 and 1
    const currentScroll = -wrapperRect.top;
    const rawProgress = Math.min(Math.max(currentScroll / totalScroll, 0), 1);

    // Map 0 -> 0.85 of scroll track to 0 -> 1.0 expansion progress
    // Video reaches 100% full screen by 85% scroll, and holds full screen for the final 15% before unpinning
    const expansionProgress = Math.min(Math.max(rawProgress / 0.85, 0), 1);

    // Cubic ease-out curve for ultra-smooth expansion and landing
    const easedProgress = 1 - Math.pow(1 - expansionProgress, 3);

    // Measure slot position relative to sticky container
    const slotRect = slotRef.current.getBoundingClientRect();
    const textTranslateY = rawProgress * -180;
    const untransformedSlotTop = slotRect.top - textTranslateY;

    const startTop = untransformedSlotTop - stickyRect.top;
    const startLeft = slotRect.left - stickyRect.left;
    const startWidth = slotRect.width;
    const startHeight = slotRect.height;

    const endTop = 0;
    const endLeft = 0;
    const endWidth = stickyRect.width;
    const endHeight = stickyRect.height;

    const currentTop = startTop + (endTop - startTop) * easedProgress;
    const currentLeft = startLeft + (endLeft - startLeft) * easedProgress;
    const currentWidth = startWidth + (endWidth - startWidth) * easedProgress;
    const currentHeight = startHeight + (endHeight - startHeight) * easedProgress;
    const currentRadius = 16 * (1 - easedProgress);

    // Apply exact pixel transforms to floating video container
    const vEl = videoContainerRef.current;
    vEl.style.top = `${currentTop}px`;
    vEl.style.left = `${currentLeft}px`;
    vEl.style.width = `${currentWidth}px`;
    vEl.style.height = `${currentHeight}px`;
    vEl.style.borderRadius = `${currentRadius}px`;

    // Apply raw scroll progress variable to CSS for text opacity and shifts
    wrapperRef.current.style.setProperty('--scroll-progress', rawProgress.toFixed(4));
  }, []);

  useEffect(() => {
    let animationFrameId;

    const handleScroll = () => {
      animationFrameId = requestAnimationFrame(updateVideoPosition);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    // Initial position trigger
    updateVideoPosition();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      cancelAnimationFrame(animationFrameId);
    };
  }, [updateVideoPosition]);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  return (
    <section className="about-scroll-wrapper" ref={wrapperRef} id="about-us">
      <div className="about-sticky-container" ref={stickyRef}>
        
        {/* Header & Description Text Content */}
        <div className="about-header-content">
          
          {/* Top Row: Tag on left, Headline on right */}
          <div className="about-top-row">
            <div className="about-badge-tag">
              <span>ABOUT DEXA INNOVATION</span>
            </div>

            <h2 className="about-headline">
              Delivering Precision CNC Machinery Designed for Performance, Productivity, and Long-Term Manufacturing Excellence
            </h2>
          </div>

          {/* Bottom Row: Paragraphs on left, 700px Video Slot on right */}
          <div className="about-bottom-row">
            <div className="about-left-text">
              <p>
                DEXA Innovation is a trusted provider of advanced CNC and industrial machinery, delivering reliable solutions that help manufacturers improve productivity, precision, and operational efficiency. We combine quality equipment with technical expertise to support businesses across diverse manufacturing industries.
              </p>
              <p>
                From understanding your production requirements to machine installation, operator training, and after-sales support, we deliver complete manufacturing solutions designed for long-term performance and business growth.
              </p>
            </div>

            {/* Target Slot Anchor for Video initial position - 700px width */}
            <div className="about-video-slot" ref={slotRef} />
          </div>

        </div>

        {/* Floating Video Container - Dynamically animated via scroll on Desktop */}
        <div className="about-video-container" ref={videoContainerRef}>
          <video
            ref={videoRef}
            className="about-video-element"
            autoPlay
            loop
            muted
            playsInline
            src={AboutVideo}
          />

          {/* Central Circular Play Button - Fades in as video expands to full screen */}
          <button 
            className="video-center-play-btn" 
            onClick={togglePlay} 
            aria-label="Toggle Play Video"
          >
            {isPlaying ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="4" width="4" height="16" rx="1"/>
                <rect x="14" y="4" width="4" height="16" rx="1"/>
              </svg>
            ) : (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                <polygon points="6 3 20 12 6 21 6 3"/>
              </svg>
            )}
          </button>

          {/* Bottom Right White Circular Play/Pause Control */}
          <button 
            className="video-control-btn" 
            onClick={togglePlay} 
            aria-label="Play Pause Control"
          >
            {isPlaying ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="4" width="4" height="16" rx="1"/>
                <rect x="14" y="4" width="4" height="16" rx="1"/>
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <polygon points="6 3 20 12 6 21 6 3"/>
              </svg>
            )}
          </button>
        </div>

      </div>
    </section>
  );
};

export default AboutSection;